import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';
import {
    DEVICES_LIST as STATIC_DEVICES_LIST,
    CONSUMABLES as STATIC_CONSUMABLES,
    STAGE_SURFACE_CONDITIONS as STATIC_STAGE_SURFACE_CONDITIONS,
    BATH_MEDIUM as STATIC_BATH_MEDIUM,
    CURRENT_TYPE as STATIC_CURRENT_TYPE,
    DEVELOPER_APPLICATION as STATIC_DEVELOPER_APPLICATION,
    FIELD_ADEQUACY as STATIC_FIELD_ADEQUACY,
    MAGNETISATION_METHOD as STATIC_MAGNETISATION_METHOD,
    OUTCOME as STATIC_OUTCOME,
    PENETRANT_APPLICATION as STATIC_PENETRANT_APPLICATION,
    SAMPLE as STATIC_SAMPLE,
    CURRENT_TYPE_POLE_SPACING as STATIC_CURRENT_TYPE_POLE_SPACING,
    DRYING_TIME_AFTER_PRECLEANING as STATIC_DRYING_TIME_AFTER_PRECLEANING,
    DWELL_TIME as STATIC_DWELL_TIME,
    DRYING_TIME_BEFORE_DEVELOPER as STATIC_DRYING_TIME_BEFORE_DEVELOPER,
    DEVELOPMENT_AND_INTERPRETATION_TIME as STATIC_DEVELOPMENT_AND_INTERPRETATION_TIME,
    RT_RADIATION_SOURCES as STATIC_RT_RADIATION_SOURCES,
} from '../constants/dropdowns';

const DropdownsContext = createContext();

// Initial state — mirrors the static fallback values exactly so the shape never changes
const INITIAL_DROPDOWNS = {
    DEVICES_LIST: STATIC_DEVICES_LIST,
    CONSUMABLES: STATIC_CONSUMABLES,
    STAGE_SURFACE_CONDITIONS: STATIC_STAGE_SURFACE_CONDITIONS,
    BATH_MEDIUM: STATIC_BATH_MEDIUM,
    CURRENT_TYPE: STATIC_CURRENT_TYPE,
    DEVELOPER_APPLICATION: STATIC_DEVELOPER_APPLICATION,
    FIELD_ADEQUACY: STATIC_FIELD_ADEQUACY,
    MAGNETISATION_METHOD: STATIC_MAGNETISATION_METHOD,
    OUTCOME: STATIC_OUTCOME,
    PENETRANT_APPLICATION: STATIC_PENETRANT_APPLICATION,
    SAMPLE: STATIC_SAMPLE,
    CURRENT_TYPE_POLE_SPACING: STATIC_CURRENT_TYPE_POLE_SPACING,
    DRYING_TIME_AFTER_PRECLEANING: STATIC_DRYING_TIME_AFTER_PRECLEANING,
    DWELL_TIME: STATIC_DWELL_TIME,
    DRYING_TIME_BEFORE_DEVELOPER: STATIC_DRYING_TIME_BEFORE_DEVELOPER,
    DEVELOPMENT_AND_INTERPRETATION_TIME: STATIC_DEVELOPMENT_AND_INTERPRETATION_TIME,
    RT_RADIATION_SOURCES: STATIC_RT_RADIATION_SOURCES,
};

export const useDropdowns = () => {
    const context = useContext(DropdownsContext);
    if (!context) {
        // Fallback to static imports if context is not available
        return {
            ...INITIAL_DROPDOWNS,
            loading: false,
            refreshDropdowns: () => Promise.resolve(),
        };
    }
    return context;
};

export const DropdownsProvider = ({ children }) => {
    const { isAuthenticated, loading: authLoading } = useAuth();
    // Single state object — one setState call → one re-render (vs. 16 separate setState calls before)
    const [dropdowns, setDropdowns] = useState(INITIAL_DROPDOWNS);
    const [loading, setLoading] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(Date.now());

    const loadDropdownsFromAPI = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/frontend-config/dropdowns/');
            const categories = response.data.categories || {};

            // Build updates object — only overwrite categories that actually arrived
            const updates = {};
            const keys = [
                'DEVICES_LIST', 'CONSUMABLES', 'STAGE_SURFACE_CONDITIONS',
                'BATH_MEDIUM', 'CURRENT_TYPE', 'DEVELOPER_APPLICATION',
                'FIELD_ADEQUACY', 'MAGNETISATION_METHOD', 'OUTCOME',
                'PENETRANT_APPLICATION', 'SAMPLE', 'CURRENT_TYPE_POLE_SPACING',
                'DRYING_TIME_AFTER_PRECLEANING', 'DWELL_TIME',
                'DRYING_TIME_BEFORE_DEVELOPER', 'DEVELOPMENT_AND_INTERPRETATION_TIME',
                'RT_RADIATION_SOURCES',
            ];
            keys.forEach(key => {
                if (Array.isArray(categories[key])) {
                    updates[key] = categories[key];
                }
            });

            // Single setState — one re-render of the entire provider subtree
            setDropdowns(prev => ({ ...prev, ...updates }));
            setLastRefresh(Date.now());
            return true;
        } catch (error) {
            console.error('Error loading dropdowns from API:', error);
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    // Load dropdowns only when user is authenticated
    useEffect(() => {
        if (authLoading || !isAuthenticated) return;
        loadDropdownsFromAPI();
    }, [loadDropdownsFromAPI, isAuthenticated, authLoading]);

    // Refresh when dropdowns are updated in another tab
    useEffect(() => {
        if (!isAuthenticated) return;
        const handleStorageChange = (e) => {
            if (e.key === 'dropdowns_updated') loadDropdownsFromAPI();
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [loadDropdownsFromAPI, isAuthenticated]);

    // Refresh on custom event
    useEffect(() => {
        if (!isAuthenticated) return;
        const handleRefresh = () => loadDropdownsFromAPI();
        window.addEventListener('dropdowns-refresh', handleRefresh);
        return () => window.removeEventListener('dropdowns-refresh', handleRefresh);
    }, [loadDropdownsFromAPI, isAuthenticated]);

    const refreshDropdowns = useCallback(() => loadDropdownsFromAPI(), [loadDropdownsFromAPI]);

    // Memoize context value so downstream consumers only re-render when data actually changes
    const value = useMemo(() => ({
        ...dropdowns,
        loading,
        refreshDropdowns,
        lastRefresh,
    }), [dropdowns, loading, refreshDropdowns, lastRefresh]);

    return (
        <DropdownsContext.Provider value={value}>
            {children}
        </DropdownsContext.Provider>
    );
};
